/* トースト通知カスタム要素 */
if (!customElements.get('aureme-toast')) {
  customElements.define(
    'aureme-toast',
    class AuremeToast extends HTMLElement {
      /* シングルトン参照 */
      static instance = null;

      /** CSS の時間文字列（'0.3s', '300ms' など）をミリ秒に変換する */
      static parseDurationToMs(value) {
        const normalizedValue = String(value || '').trim();
        if (!normalizedValue) return 0;

        if (normalizedValue.endsWith('ms')) {
          return parseFloat(normalizedValue) || 0;
        }

        if (normalizedValue.endsWith('s')) {
          return (parseFloat(normalizedValue) || 0) * 1000;
        }

        return parseFloat(normalizedValue) || 0;
      }

      /** 要素の CSS transition 合計時間をミリ秒で返す（transitionend 未発火時のフォールバック用） */
      static getTransitionFallbackMs(element, fallbackMs = 500) {
        if (!element) return fallbackMs;

        const styles = window.getComputedStyle(element);
        const durations = styles.transitionDuration.split(',').map(AuremeToast.parseDurationToMs);
        const delays = styles.transitionDelay.split(',').map(AuremeToast.parseDurationToMs);
        const maxTransitionMs = durations.reduce((maxDuration, duration, index) => {
          const delay = delays[index] ?? delays[delays.length - 1] ?? 0;
          return Math.max(maxDuration, duration + delay);
        }, 0);

        return maxTransitionMs > 0 ? Math.ceil(maxTransitionMs) + 50 : fallbackMs;
      }

      autoHideTimer = null;
      pendingTransitionEnd = null;
      pendingOpenFrame = null;
      pendingHideFallbackTimer = null;

      handleCloseClick = () => {
        this.hide();
      };

      handleKeydown = (event) => {
        if (event.key !== 'Escape') return;
        this.hide();
      };

      connectedCallback() {
        AuremeToast.instance = this;

        this.toastElement = this.querySelector('.aureme-toast');
        this.messageElement = this.querySelector('.aureme-toast__message');
        this.closeButton = this.querySelector('.aureme-toast__close');

        this.closeButton?.addEventListener('click', this.handleCloseClick);
        document.addEventListener('keydown', this.handleKeydown);
      }

      disconnectedCallback() {
        if (AuremeToast.instance === this) {
          AuremeToast.instance = null;
        }
        this.closeButton?.removeEventListener('click', this.handleCloseClick);
        document.removeEventListener('keydown', this.handleKeydown);
        clearTimeout(this.autoHideTimer);
        this.cancelPendingOpenFrame();
        this.clearPendingTransition();
      }

      getTransitionFallbackMs(fallbackMs = 500) {
        return AuremeToast.getTransitionFallbackMs(this.toastElement, fallbackMs);
      }

      /**
       * 任意のJSからトーストを表示する静的メソッド
       * @param {{ message: string, type?: 'error' | 'success' }} options
       */
      static show({ message, type = 'error' } = {}) {
        const instance = AuremeToast.instance;
        if (!instance) return;
        instance.open(message, type);
      }

      open(message, type) {
        if (!this.toastElement || !this.messageElement) return;

        clearTimeout(this.autoHideTimer);

        /* メッセージ設定 */
        this.messageElement.textContent = message;

        /* ARIA属性をタイプに応じて切り替え */
        if (type === 'error') {
          this.toastElement.setAttribute('role', 'alert');
          this.toastElement.setAttribute('aria-live', 'assertive');
        } else {
          this.toastElement.setAttribute('role', 'status');
          this.toastElement.setAttribute('aria-live', 'polite');
        }

        /* 閉じアニメーション中の古いリスナーを除去 */
        this.clearPendingTransition();
        this.cancelPendingOpenFrame();

        /* animate → 次フレームで active を付与してトランジション発火 */
        this.toastElement.classList.add('animate');
        this.pendingOpenFrame = requestAnimationFrame(() => {
          this.pendingOpenFrame = null;
          if (!this.toastElement.classList.contains('animate')) return;
          this.toastElement.classList.add('active');
        });

        /* success時は5秒後に自動消去 */
        if (type === 'success') {
          this.autoHideTimer = setTimeout(() => this.hide(), 5000);
        }
      }

      hide() {
        if (!this.toastElement) return;

        clearTimeout(this.autoHideTimer);
        this.cancelPendingOpenFrame();
        this.clearPendingTransition();
        this.toastElement.classList.remove('active');

        const finalizeHide = () => {
          clearTimeout(this.pendingHideFallbackTimer);
          this.toastElement.removeEventListener('transitionend', finalizeHide);
          this.toastElement.classList.remove('animate');
          this.pendingTransitionEnd = null;
          this.pendingHideFallbackTimer = null;
        };
        this.pendingTransitionEnd = finalizeHide;
        this.toastElement.addEventListener('transitionend', this.pendingTransitionEnd, {
          once: true,
        });
        /* transitionend が飛ばないケースでも表示用クラスを残さない */
        this.pendingHideFallbackTimer = window.setTimeout(
          finalizeHide,
          this.getTransitionFallbackMs(),
        );
      }

      cancelPendingOpenFrame() {
        if (!this.pendingOpenFrame) return;
        cancelAnimationFrame(this.pendingOpenFrame);
        this.pendingOpenFrame = null;
      }

      clearPendingTransition() {
        if (!this.pendingTransitionEnd) return;
        this.toastElement.removeEventListener('transitionend', this.pendingTransitionEnd);
        clearTimeout(this.pendingHideFallbackTimer);
        this.toastElement.classList.remove('animate');
        this.pendingTransitionEnd = null;
        this.pendingHideFallbackTimer = null;
      }
    },
  );
}

/* グローバルから AuremeToast.show() で呼び出せるようにする */
window.AuremeToast = customElements.get('aureme-toast');
